import React, { useContext, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Grid, Icon } from 'semantic-ui-react';
import { CardData, CardDataItem, Modal, ModulesFilter, VALIDATION_PATTERNS } from 'dyl-components';

import AddLeadModal from "shared/modals/AddLeadModal";
import RestrictedView from 'shared/RestrictedView';
import { USER_PERMISSIONS } from 'dyl-components';

import {
    KEY_CREATED, KEY_CREATED_BY, KEY_OWNER_ID,
    KEY_LAST_MODIFIED, KEY_PRODUCT_INTEREST,
    KEY_LAST_MODIFIED_BY, KEY_LEAD_PRIORITY,
    KEY_LOST_REASON,
    KEY_OUTCOME, KEY_LEAD_ID, KEY_LEAD_SCORE, KEY_CONVERT,
    KEY_CONTACT_NAME, KEY_LEAD_RATING, KEY_ACTIONS, KEY_ACCOUNT,
    KEY_MASTER_SECONDARY, KEY_STAGE, KEY_CAMPAIGN_SOURCE,
    KEY_CO_OWNER,
    KEY_DATA_PROVIDER,
    KEY_CALLBACK
} from 'shared/constants/MODULE_COLUMNS';
import FullScreenModalPopUp from 'shared/FullScreenModalPopUp';
import { BulkActionsContext } from 'shared/context/BulkActionsProvider';
import ContactBulkActionsModal from 'shared/modals/ContactBulkActionsModal';
import leadsActions from 'actions/leads';
import { useNavigate, useSearchParams } from 'react-router-dom';
const COLUMN_ROWS = [
    [
        { value: KEY_CREATED, label: "Created" },
        { value: KEY_LOST_REASON, label: "Lost Reason/Type" },
        { value: KEY_CREATED_BY, label: "Created by" }
    ],
    [
        { value: KEY_OWNER_ID, label: "Owned By" },
        { value: KEY_CO_OWNER, label: "Co-Owned By" },
        { value: KEY_LAST_MODIFIED, label: "Last Modified" },
    ],
    [
        { value: KEY_LAST_MODIFIED_BY, label: "Last Modified By" },
        { value: KEY_LEAD_PRIORITY, label: "Priority", disabled: true },
        { value: KEY_OUTCOME, label: "Outcome" }
    ],
    [
        { value: KEY_LEAD_ID, label: "Lead ID", isForcedChecked: true },
        { value: KEY_LEAD_SCORE, label: "Score", disabled: true },
        { value: KEY_CONVERT, label: "Convert" }
    ],
    [
        { value: KEY_CONTACT_NAME, label: "Contact Name", isForcedChecked: true },
        { value: KEY_LEAD_RATING, label: "Rating", disabled: true },
        { value: KEY_ACTIONS, label: "Actions" }
    ],
    [
        { value: KEY_ACCOUNT, label: "Account" },
        { value: KEY_MASTER_SECONDARY, label: "Master/Secondary Source" },
        { value: KEY_STAGE, label: "Stage" },
    ],
    [
        { value: KEY_CAMPAIGN_SOURCE, label: "Campaign Converted" },
        { value: KEY_PRODUCT_INTEREST, label: "Product Interests" },
        { value: KEY_DATA_PROVIDER, label: "Data Provider/ Data Record Name" }
    ],
    [
        { value: KEY_CALLBACK, label: "Callback" }

    ]
];

const LeadsToolbar = ({ search, onSearchSubmit, cancelFunction, onChangeSearch, isColumnHidden, onToggleColumn }) => {
    const { isReadingLeads, isReadingDisplay } = useSelector(state => ({
        isReadingLeads: state.leads.isReadingLeads
    }));
    const [selectedLeads, , areLeadsInAllPagesSelected] = useContext(BulkActionsContext);
    const { count, lead_connect_count= 0 } = useSelector(state => state.leads);
    const [isDirty, setIsDirty] = useState(false);
    const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
    const [isAddLeadModalOpen, setIsAddLeadModalOpen] = useState(false);
    const [isBulkActionsModalOpen, setIsBulkActionsModalOpen] = useState(false);
    const [params] = useSearchParams();

    const isFilterActive = params.get('lead_connect') === 'true';

    const hasSelectedLeads = selectedLeads.length || areLeadsInAllPagesSelected;
    const numberOfSelectedLeads = areLeadsInAllPagesSelected ? count : selectedLeads.length;
    const onOpenAddLeadModal = () => {
        setIsAddLeadModalOpen(true);
    }

    const onCloseAddLeadModal = () => {
        setIsAddLeadModalOpen(false);
    }
    const onOpenBulkActionsModal = () => {
        setIsBulkActionsModalOpen(true);
    }

    const onCloseBulkActionsModal = () => {
        setIsBulkActionsModalOpen(false);
    }
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const onToggleLeadConnectFilter = () => {
        const query = new URLSearchParams(params);
        const displayedColumns = query.get('columns')?.split(',') || [];
        const toDisplay = (() => {
            if (!isFilterActive) {
                return [...new Set([
                    ...displayedColumns,
                    KEY_CREATED, 
                    KEY_LEAD_ID, 
                    KEY_DATA_PROVIDER, 
                    KEY_STAGE, 
                    KEY_CALLBACK, 
                    KEY_ACTIONS
                ])]
            }
            return displayedColumns;
        })()
        if (isFilterActive) {
            query.delete('lead_connect');
        } else {
            query.set('lead_connect', true);
        }
        query.set('columns', toDisplay.toString());
        query.set('page', 1);
        const query_string = query.toString();
        navigate(`/leads${query_string ? `?${query_string}` : ''}`,);
    }

    return (
        <React.Fragment>
            <Grid>
                <Grid.Row columns='equal' verticalAlign='middle'>
                    <Grid.Column width={5}>
                        <ModulesFilter
                            isLoading={isReadingLeads || isReadingDisplay}
                            search={search}
                            onSearchSubmit={onSearchSubmit}
                            cancelFunction={cancelFunction}
                            onChangeSearch={onChangeSearch}
                            columnRows={COLUMN_ROWS}
                            columnCount={3}
                            isColumnHidden={isColumnHidden}
                            onToggleColumn={onToggleColumn}
                            validationPattern={VALIDATION_PATTERNS.alphanumeric_w_special_chars_for_universal_search}
                        />
                    </Grid.Column>
                    <Grid.Column width={8} floated='right'>
                        <RestrictedView type="disabled" userPermission={USER_PERMISSIONS.LEAD_ADD}>
                            <Button floated='right' className='ModulesListToolbar__NewModulesListButton' onClick={onOpenAddLeadModal}>
                                <Icon className='plus' /> New Lead
                            </Button>
                        </RestrictedView>
                        <RestrictedView type="disabled" userPermission={USER_PERMISSIONS.LEAD_ADD}>
                            <Button floated='right' className='ModulesListToolbar__BulkActionButton' disabled={!hasSelectedLeads} onClick={onOpenBulkActionsModal}>
                                Bulk Action
                            </Button>
                        </RestrictedView>
                        <div className='ModulesListToolbar__LeadConnect'>
                            <div className='ModulesListToolbar__LeadConnect-button' onClick={!isFilterActive ? onToggleLeadConnectFilter : () => {}}>
                            <CardData active={isFilterActive} onClick={() => {}} style={{padding: 100}}>
                                <CardDataItem>
                                    <Icon style={{ margin: '0 5px' }} className={"fas fa-file-user"} /> <strong>{"Lead Connect"}: </strong>&nbsp;<span className={`FilterValue`}> {lead_connect_count} </span>
                                    {isFilterActive &&
                                        <Icon style={{ marginLeft: 10 }} className='fa-solid fa-circle-x' link onClick={onToggleLeadConnectFilter} />
                                    }
                                </CardDataItem>
                            </CardData>
                            </div>
                        </div>
                    </Grid.Column>
                </Grid.Row>
            </Grid>
            <Modal open={isAddLeadModalOpen} onClose={() => {
                isDirty
                    ? setIsConfirmationOpen(true)
                    : onCloseAddLeadModal();
            }}>
                <AddLeadModal onOpen={() => { return Promise.resolve(); }} onClose={onCloseAddLeadModal} onDirtyChange={(dirty) => setIsDirty(dirty)} />
            </Modal>
            <FullScreenModalPopUp
                header={"Changes not saved"}
                subheader={"Are you sure you want to exit?"}
                isOpen={isConfirmationOpen}
                onConfirm={() => {
                    onCloseAddLeadModal();
                    setIsConfirmationOpen(false);
                    setIsDirty(false)
                }}
                onFormClose={() => setIsConfirmationOpen(false)}
                closeIcon={false}
            />
            <ContactBulkActionsModal
                open={isBulkActionsModalOpen}
                onClose={onCloseBulkActionsModal}
                numberSelectedContacts={numberOfSelectedLeads}
                module="lead"
                refresh={() => {
                    let columns = (params.get('columns')?.split(',') || [
                        KEY_CREATED,
                        KEY_LEAD_ID,
                        KEY_OUTCOME,
                        KEY_ACCOUNT,
                        KEY_STAGE,
                        KEY_CONVERT,
                        KEY_ACTIONS
                    ]).filter(
                        column => ![KEY_CONVERT, KEY_ACTIONS, KEY_CONTACT_NAME].includes(column)
                    );

                    dispatch(leadsActions.readLeads({
                        ...Object.fromEntries(params),
                        columns: columns.toString(),
                        page: 1,
                        limit: 25,
                    }));
                }}
            />
        </React.Fragment>
    );
}

export default LeadsToolbar;
